import { CameraOff, ShieldAlert, VideoOff } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Spinner } from '@/components/ui/spinner';
import type { PreviewStatus } from '@/hooks/useMediaPreview';
import { cn, initials } from '@/lib/utils';

/**
 * The self-view. Mirrored, because an un-mirrored preview of your own face is
 * the single most common "the camera is broken" report — every other app you
 * have used mirrors it.
 */
export function VideoPreview({
  track,
  status,
  problem,
  cameraEnabled,
  displayName,
  children,
}: {
  track: MediaStreamTrack | null;
  status: PreviewStatus;
  problem: string | null;
  cameraEnabled: boolean;
  displayName: string;
  children?: React.ReactNode;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    element.srcObject = track ? new MediaStream([track]) : null;
    return () => {
      element.srcObject = null;
    };
  }, [track]);

  const blocked = status === 'denied' || status === 'unavailable';

  return (
    // Sized by aspect ratio below lg and capped by viewport height, so on a
    // phone the face, the toggles and the mic meter fit above the fold.
    <div className="relative isolate aspect-[4/3] max-h-[45svh] w-full overflow-hidden bg-[oklch(0.12_0.015_265)] sm:aspect-video sm:max-h-[55svh] lg:aspect-auto lg:h-full lg:max-h-none lg:min-h-[30rem]">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={cn(
          'size-full scale-x-[-1] object-cover transition-opacity duration-300',
          track && cameraEnabled ? 'opacity-100' : 'opacity-0',
        )}
      />

      {/* Everything below is the fallback stack, layered over the video so a
          track arriving mid-render never leaves a blank black box. */}
      {!(track && cameraEnabled) && (
        <div className="absolute inset-0 grid place-items-center px-6 text-center">
          {status === 'requesting' && (
            <div className="text-muted-foreground flex flex-col items-center gap-3">
              <Spinner />
              <p className="text-sm">Waiting for camera permission…</p>
            </div>
          )}

          {blocked && (
            <div className="flex max-w-xs flex-col items-center gap-3">
              <span className="grid size-12 place-items-center rounded-2xl border border-[color-mix(in_oklab,var(--warning)_35%,transparent)] bg-[color-mix(in_oklab,var(--warning)_12%,transparent)]">
                <ShieldAlert className="size-5.5 text-[var(--warning)]" />
              </span>
              <p className="text-muted-foreground text-sm text-pretty">{problem}</p>
            </div>
          )}

          {status === 'ready' && !cameraEnabled && (
            <div className="flex flex-col items-center gap-3">
              <Avatar className="size-20 ring-1 ring-white/10">
                <AvatarFallback className="text-xl">{initials(displayName)}</AvatarFallback>
              </Avatar>
              <p className="text-muted-foreground flex items-center gap-2 text-sm">
                <CameraOff className="size-4" />
                Camera is off
              </p>
            </div>
          )}

          {status === 'ready' && cameraEnabled && !track && (
            <div className="text-muted-foreground flex flex-col items-center gap-3">
              <VideoOff className="size-5" />
              <p className="text-sm text-pretty">{problem ?? 'Starting your camera…'}</p>
            </div>
          )}
        </div>
      )}

      {/* Keeps the overlaid controls readable over a bright, backlit room. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-linear-to-t from-black/70 to-transparent" />

      {children}
    </div>
  );
}
